import { apiAdmin } from "./api";

const hasQuestionId = (question) => {
  const id = question?.id ?? question?.question_id;
  return id !== null && id !== undefined && id !== "";
};

const getQuestionId = (question) => question?.id ?? question?.question_id;

const getQuestionText = (question) => {
  if (typeof question === "string") return question.trim();
  return String(question?.question || "").trim();
};

const getQuestionAnswers = (question) => {
  const source = Array.isArray(question?.answers)
    ? question.answers
    : Array.isArray(question?.options)
    ? question.options
    : [];

  return source
    .map((answer) => {
      if (typeof answer === "object" && answer !== null) {
        return String(answer.answer ?? answer.value ?? answer.label ?? "").trim();
      }

      return String(answer ?? "").trim();
    })
    .filter(Boolean);
};

const getApiErrorMessage = (responseData, fallback) =>
  responseData?.action || responseData?.message || responseData?.error || fallback;

const ensureSuccess = (response, fallback) => {
  if (response?.data?.status) return response.data;
  throw new Error(getApiErrorMessage(response?.data, fallback));
};

const getQuestionSignature = (question) => {
  const qType =
    question?.type === "Checklist"
      ? "Multiple Choice"
      : question?.type ||
        (getQuestionAnswers(question).length > 0
          ? "Single Choice"
          : "Short Answer");

  const rawCharLimit = question?.character_limit;
  const hasCharLimit =
    qType === "Long Answer" &&
    rawCharLimit !== undefined &&
    rawCharLimit !== null &&
    String(rawCharLimit).trim() !== "" &&
    !isNaN(parseInt(rawCharLimit, 10)) &&
    parseInt(rawCharLimit, 10) > 0;

  const hasMaxPhotos = qType === "Photo";

  return JSON.stringify({
    question: getQuestionText(question),
    type: qType,
    answers: getQuestionAnswers(question),
    character_limit: hasCharLimit ? String(parseInt(rawCharLimit, 10)) : null,
    max_photos: hasMaxPhotos ? "1" : null,
  });
};

/**
 * Converts API questions ({ question, answers }) to the Agenda question editor
 * model ({ question, type, options }) while retaining the backend question id.
 */
export const normalizeAgendaQuestionsForForm = (questions = []) =>
  (Array.isArray(questions) ? questions : [])
    .map((question) => {
      const answers = getQuestionAnswers(question);
      const id = getQuestionId(question);

      const rawCharLimit =
        question?.character_limit !== undefined &&
        question?.character_limit !== null &&
        question?.character_limit !== ""
          ? Number(question.character_limit)
          : null;

      const rawType = question?.type;
      let qType = rawType;
      if (!qType) {
        qType = answers.length > 0 ? "Single Choice" : "Short Answer";
      } else if (qType === "Checklist") {
        qType = "Multiple Choice";
      }

      const hasCharLimit =
        qType === "Long Answer" &&
        rawCharLimit !== null &&
        !isNaN(rawCharLimit) &&
        rawCharLimit > 0;

      const hasMaxPhotos = qType === "Photo";

      return {
        ...(typeof question === "object" && question !== null ? question : {}),
        ...(hasQuestionId(question) ? { id } : {}),
        question: getQuestionText(question),
        type: qType,
        options: answers,
        answers,
        character_limit: hasCharLimit ? String(rawCharLimit) : null,
        max_photos: hasMaxPhotos ? "1" : null,
      };
    })
    .filter((question) => question.question);

export const createAgendaEventQuestions = async (eventId, questions = []) => {
  const payloadQuestions = normalizeAgendaQuestionsForForm(questions)
    .map((question) => {
      const rawType =
        question.type && String(question.type).trim() !== ""
          ? String(question.type).trim()
          : null;
      let qType = rawType;
      if (!qType) {
        qType = question.answers?.length > 0 ? "Single Choice" : "Short Answer";
      } else if (qType === "Checklist") {
        qType = "Multiple Choice";
      }

      const item = {
        question: question.question,
        type: qType,
        answers: Array.isArray(question.answers) ? question.answers : [],
      };

      const rawCharLimit = question.character_limit;
      if (
        qType === "Long Answer" &&
        rawCharLimit !== undefined &&
        rawCharLimit !== null &&
        String(rawCharLimit).trim() !== ""
      ) {
        const parsed = parseInt(rawCharLimit, 10);
        if (!isNaN(parsed) && parsed > 0) {
          item.character_limit = String(parsed);
        }
      }

      if (qType === "Photo") {
        item.max_photos = "1";
      }

      return item;
    })
    .filter((question) => question.question);

  if (payloadQuestions.length === 0) return null;
  if (!eventId) throw new Error("The event was created but no event id was returned for its questions.");

  const response = await apiAdmin.post("user/event/questions/add", {
    event_id: eventId,
    questions: payloadQuestions,
  });

  return ensureSuccess(response, "Unable to create event questions");
};

/**
 * Persists only changed questions after the event itself has been updated.
 * Existing questions are updated/deleted individually; new ones are added in
 * one request so the add endpoint never duplicates the full existing list.
 */
export const syncAgendaEventQuestions = async ({
  eventId,
  initialQuestions = [],
  questions = [],
}) => {
  const initial = normalizeAgendaQuestionsForForm(initialQuestions);
  const current = normalizeAgendaQuestionsForForm(questions);
  const initialById = new Map(
    initial
      .filter(hasQuestionId)
      .map((question) => [String(getQuestionId(question)), question])
  );
  const currentById = new Map(
    current
      .filter(hasQuestionId)
      .map((question) => [String(getQuestionId(question)), question])
  );
  const deletedQuestions = initial.filter(
    (question) =>
      hasQuestionId(question) && !currentById.has(String(getQuestionId(question)))
  );
  const updatedQuestions = current.filter((question) => {
    if (!hasQuestionId(question)) return false;
    const initialQuestion = initialById.get(String(getQuestionId(question)));
    return (
      initialQuestion &&
      getQuestionSignature(initialQuestion) !== getQuestionSignature(question)
    );
  });
  const newQuestions = current.filter(
    (question) => !initialById.has(String(getQuestionId(question)))
  );

  if (
    deletedQuestions.length === 0 &&
    updatedQuestions.length === 0 &&
    newQuestions.length === 0
  ) {
    return { added: 0, updated: 0, deleted: 0 };
  }

  if (!eventId) {
    throw new Error("An event id is required to save event questions.");
  }

  await Promise.all([
    ...deletedQuestions.map(async (question) => {
      const response = await apiAdmin.get(
        `user/event/questions/delete/${getQuestionId(question)}`
      );
      return ensureSuccess(response, "Unable to delete an event question");
    }),
    ...updatedQuestions.map(async (question) => {
      const qId = getQuestionId(question);
      const rawType =
        question.type && String(question.type).trim() !== ""
          ? String(question.type).trim()
          : null;
      let qType = rawType;
      if (!qType) {
        qType = question.answers?.length > 0 ? "Single Choice" : "Short Answer";
      } else if (qType === "Checklist") {
        qType = "Multiple Choice";
      }
      const qAnswers = Array.isArray(question.answers) ? question.answers : [];

      const rawCharLimit = question.character_limit;
      const parsedCharLimit =
        qType === "Long Answer" &&
        rawCharLimit !== undefined &&
        rawCharLimit !== null &&
        String(rawCharLimit).trim() !== "" &&
        !isNaN(parseInt(rawCharLimit, 10)) &&
        parseInt(rawCharLimit, 10) > 0
          ? String(parseInt(rawCharLimit, 10))
          : undefined;

      const parsedMaxPhotos = qType === "Photo" ? "1" : undefined;

      const singleQuestionPayload = {
        id: qId,
        question_id: qId,
        question: question.question,
        type: qType,
        answers: qAnswers,
        ...(parsedCharLimit !== undefined ? { character_limit: parsedCharLimit } : {}),
        ...(parsedMaxPhotos !== undefined ? { max_photos: parsedMaxPhotos } : {}),
      };

      const updatePayload = {
        event_id: eventId,
        question_id: qId,
        id: qId,
        question: question.question,
        type: qType,
        answers: qAnswers,
        empty_array: qAnswers.length === 0,
        questions: [singleQuestionPayload],
        ...(parsedCharLimit !== undefined ? { character_limit: parsedCharLimit } : {}),
        ...(parsedMaxPhotos !== undefined ? { max_photos: parsedMaxPhotos } : {}),
      };

      const response = await apiAdmin.post("user/event/questions/update", updatePayload);
      return ensureSuccess(response, "Unable to update an event question");
    }),
    newQuestions.length > 0
      ? createAgendaEventQuestions(eventId, newQuestions)
      : Promise.resolve(),
  ]);

  return {
    added: newQuestions.length,
    updated: updatedQuestions.length,
    deleted: deletedQuestions.length,
  };
};
